// The first steps of a scenario A project, shown in the editor area until
// there is something to review: upload the .proto, choose the root message
// (manifest.yaml), then start the AI classification.
import { useRef, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { Icon } from "./icons";
import { t } from "../i18n";
import type { Actions, SetupForm, SetupGeo, WorkbenchData } from "./model";

/** Protocol logic and connectivity (GIOTHUB-31): the set-up fields shown
 *  only with the logic types switch on. */
interface LogicForm {
  protocol: "" | "logic";
  module: string;
  contract: string;
  params: string;
  downlink: "" | "always" | "on_uplink";
  window: string;
}

const noLogic: LogicForm = { protocol: "", module: "", contract: "^1", params: "", downlink: "", window: "10" };

/** The set-up request's logic fields; throws on bad params JSON. */
function logicFields(f: LogicForm): Partial<SetupForm> {
  const out: Partial<SetupForm> = {};
  if (f.protocol === "logic") {
    let params: Record<string, unknown> | undefined;
    if (f.params.trim()) {
      try {
        params = JSON.parse(f.params);
      } catch {
        throw new Error(t("setup.logic.badparams"));
      }
    }
    out.protocol = "logic";
    out.logic = { module: f.module, version: f.contract, ...(params ? { params } : {}) };
  }
  if (f.downlink) out.connectivity = { downlink: f.downlink, ...(f.downlink === "on_uplink" ? { window_s: Number(f.window) } : {}) };
  return out;
}

function LogicFields({ data, value, set, writer, protocolChoice }:
  { data: WorkbenchData; value: LogicForm; set: (f: LogicForm) => void; writer: boolean; protocolChoice: boolean }) {
  if (!data.logicTypes) return null;
  const mods = data.modules ?? [];
  const mod = mods.find((m) => m.id === value.module);
  // a module brings its newest contract and its downlink handling
  const withModule = (base: LogicForm, id: string): LogicForm => {
    const m = mods.find((x) => x.id === id);
    return { ...base, module: id, contract: m ? `^${m.contract_majors[m.contract_majors.length - 1]}` : "^1",
      downlink: (m?.connectivity?.downlink as LogicForm["downlink"]) ?? base.downlink, window: String(m?.connectivity?.window_s ?? base.window) };
  };
  const pick = (id: string) => set(withModule(value, id));
  return (
    <>
      {protocolChoice && (
        <fieldset class="m-0 flex flex-col gap-2 border-0 p-0 sm:col-span-2">
          <span class="text-[12px] font-semibold text-ink2">{t("setup.protocol")}</span>
          <label class="flex items-start gap-2 text-[13px]">
            <input type="radio" name="protocol" checked={value.protocol === ""} disabled={!writer} onChange={() => set({ ...value, protocol: "" })} />
            <span><b>{t("setup.protocol.payloads.name")}</b>{t("setup.protocol.payloads.text")}</span>
          </label>
          <label class="flex items-start gap-2 text-[13px]">
            <input type="radio" name="protocol" checked={value.protocol === "logic"} disabled={!writer}
              onChange={() => set(withModule({ ...value, protocol: "logic" }, value.module || mods[0]?.id || ""))} />
            <span><b>{t("setup.protocol.logic.name")}</b>{t("setup.protocol.logic.text")}{" "}
              <a href="/help/logic-types" target="_blank">{t("setup.protocol.logic.manual")}</a></span>
          </label>
        </fieldset>
      )}
      {protocolChoice && value.protocol === "logic" && (
        <>
          <label class="flex flex-col gap-1">
            <span class="text-[12px] font-semibold text-ink2">{t("setup.protocol.logic.name")}</span>
            <select class="field font-mono" value={value.module} disabled={!writer} onChange={(e) => pick((e.target as HTMLSelectElement).value)}>
              {mods.map((m) => <option value={m.id}>{m.id}</option>)}
            </select>
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-[12px] font-semibold text-ink2">{t("setup.logic.contract")}</span>
            <select class="field font-mono" value={value.contract} disabled={!writer} onChange={(e) => set({ ...value, contract: (e.target as HTMLSelectElement).value })}>
              {(mod?.contract_majors ?? [1]).map((n) => <option value={`^${n}`}>{`^${n}`}</option>)}
            </select>
          </label>
          {mod && <p class="m-0 text-[12.5px] text-muted sm:col-span-2">{mod.name}: {mod.description}</p>}
          {mod && Object.keys(mod.params?.properties ?? {}).length > 0 && (
            <label class="flex flex-col gap-1 sm:col-span-2">
              <span class="text-[12px] font-semibold text-ink2">{t("setup.logic.params")}</span>
              <textarea class="field font-mono" rows={3} value={value.params} disabled={!writer}
                onInput={(e) => set({ ...value, params: (e.target as HTMLTextAreaElement).value })} />
            </label>
          )}
        </>
      )}
      <label class="flex flex-col gap-1">
        <span class="text-[12px] font-semibold text-ink2">{t("setup.downlink")}</span>
        <select class="field" value={value.downlink} disabled={!writer} onChange={(e) => set({ ...value, downlink: (e.target as HTMLSelectElement).value as LogicForm["downlink"] })}>
          <option value="">{t("setup.downlink.none")}</option>
          <option value="always">{t("setup.downlink.always")}</option>
          <option value="on_uplink">{t("setup.downlink.onuplink")}</option>
        </select>
      </label>
      {value.downlink === "on_uplink" ? (
        <label class="flex flex-col gap-1">
          <span class="text-[12px] font-semibold text-ink2">{t("setup.downlink.window")}</span>
          <input class="field font-mono" type="number" min={1} max={3600} required value={value.window} disabled={!writer}
            onInput={(e) => set({ ...value, window: (e.target as HTMLInputElement).value })} />
        </label>
      ) : <span class="hidden sm:block" />}
    </>
  );
}

function Step({ n, title, done, children }: { n: number; title: string; done?: boolean; children?: ComponentChildren }) {
  return (
    <section class="flex gap-4">
      <span class={`mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-full text-[12.5px] font-bold ${done ? "bg-good-soft text-good" : "bg-sel text-accent2"}`}>
        {done ? <Icon name="check" size={14} stroke={2.6} /> : n}
      </span>
      <div class="min-w-0 flex-1">
        <h3 class="m-0 text-[15px] font-semibold">{title}</h3>
        {children}
      </div>
    </section>
  );
}

export const DECODER_PROMPT =
  "Write the JavaScript decoder of this device type from the uploaded documents and my examples: read the payload documentation, " +
  "write decode.js, propose the catalogue keys it emits, run the examples until all of mine pass, add edge-case examples, ask me where " +
  "the documents leave it open, then validate and write ai_guide.md.";

export const CLASSIFY_PROMPT =
  "Classify the .proto of this device type for the gIoT platform: read the schema, the uploaded documents and the vocabulary, " +
  "propose the hints of every field and message, ask me where the sources leave it open, then validate and write ai_guide.md.";

// GeoSetup: the manifest's geolocation block (geo tags switch, GIOTHUB-64).
// Sent only when the person changed it, so a set-up never rewrites a block
// it did not show completely.
interface GeoForm extends SetupGeo { touched: boolean }

function geoForm(data: WorkbenchData): GeoForm {
  return { enabled: false, inputs: [], reported: false, ...(data.geolocation ?? {}), touched: false };
}

function geoFields(data: WorkbenchData, g: GeoForm): Partial<SetupForm> {
  if (!data.geoTags || !g.touched) return {};
  const { touched: _, ...geo } = g;
  return { geolocation: { ...geo, uere_m: geo.uere_m || undefined } };
}

function GeoSetup({ data, value, set, writer }: { data: WorkbenchData; value: GeoForm; set: (g: GeoForm) => void; writer: boolean }) {
  if (!data.geoTags) return null;
  const upd = (p: Partial<GeoForm>) => set({ ...value, ...p, touched: true });
  const toggle = (i: string) => upd({ inputs: value.inputs.includes(i) ? value.inputs.filter((x) => x !== i) : [...value.inputs, i] });
  return (
    <fieldset class="m-0 flex flex-col gap-2 rounded-lg border border-line px-3 py-2.5 sm:col-span-2" disabled={!writer}>
      <legend class="px-1 text-[12px] font-semibold text-ink2">{t("setup.geo.legend")}</legend>
      <label class="flex items-start gap-2.5 text-[13px]">
        <input type="checkbox" class="mt-1" checked={!!value.reported} onChange={() => upd({ reported: !value.reported })} />
        <span>{t("setup.geo.reported")}<span class="block text-[12px] text-muted">{t("setup.geo.reported.hint")}</span></span>
      </label>
      <label class="flex items-start gap-2.5 text-[13px]">
        <input type="checkbox" class="mt-1" checked={value.enabled} onChange={() => upd({ enabled: !value.enabled })} />
        <span>{t("setup.geo.enabled")}<span class="block text-[12px] text-muted">{t("setup.geo.enabled.hint")}</span></span>
      </label>
      {value.enabled && (
        <div class="flex flex-wrap gap-3 pl-6 text-[13px]">
          {[["wifi", t("setup.geo.wifi")], ["cell", t("setup.geo.cell")], ["ble", t("setup.geo.ble")]].map(([k, l]) => (
            <label class="flex items-center gap-1.5"><input type="checkbox" checked={value.inputs.includes(k!)} onChange={() => toggle(k!)} />{l}</label>
          ))}
        </div>
      )}
      <label class="flex flex-col gap-1">
        <span class="text-[12px] font-semibold text-ink2">{t("setup.geo.uere")} <span class="font-normal text-dim">{t("setup.geo.uere.hint")}</span></span>
        <input class="field w-32 font-mono" inputMode="decimal" value={value.uere_m ?? ""} placeholder="5"
          onInput={(e) => { const n = Number((e.target as HTMLInputElement).value); upd({ uere_m: Number.isFinite(n) && n > 0 ? n : undefined }); }} />
      </label>
    </fieldset>
  );
}

export function SetupPanel({ data, actions, writer }: { data: WorkbenchData; actions: Actions; writer: boolean }) {
  if (data.setup?.scenario === "C" || data.setup?.scenario === "B") return <SetupPanelC data={data} actions={actions} writer={writer} />;
  return <SetupPanelA data={data} actions={actions} writer={writer} />;
}

function SetupPanelC({ data, actions, writer }: { data: WorkbenchData; actions: Actions; writer: boolean }) {
  const b = data.setup?.scenario === "B";
  // scenario C: the vendor is the device's maker, not the organisation building the type
  const [form, setForm] = useState({ version: "1.0.0", vendor: b ? data.me.org : "", description: "", categories: "", license: "proprietary", website: "", support_email: "" });
  const [lf, setLf] = useState<LogicForm>(noLogic);
  const [gf, setGf] = useState<GeoForm>(() => geoForm(data));
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await actions.setup({ ...form, ...logicFields(lf), ...geoFields(data, gf), root: "", categories: form.categories.split(",").map((c) => c.trim().toLowerCase()).filter(Boolean) });
    } catch (x) {
      setErr((x as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div class="min-h-0 flex-1 overflow-y-auto">
      <div class="mx-auto flex max-w-[760px] flex-col gap-7 px-6 py-10">
        <header>
          <h2 class="m-0 text-[20px] font-semibold">{t("setup.title", { name: data.project.name })}</h2>
          {b ? (
            <p class="mb-0 mt-1.5 text-muted">{t("setup.b.intro")}{" "}<a href="/help/scenario-b" target="_blank">{t("setup.manual.steps")}</a>.</p>
          ) : (
            <p class="mb-0 mt-1.5 text-muted">{t("setup.c.intro")}{" "}<a href="/help/scenario-c" target="_blank">{t("setup.manual.steps")}</a>.</p>
          )}
        </header>
        <Step n={1} title={t("setup.step.create")}>
          <form onSubmit={submit} class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label class="flex flex-col gap-1">
              <span class="text-[12px] font-semibold text-ink2">{t("setup.version")}</span>
              <input class="field font-mono" required pattern="[0-9]+\.[0-9]+\.[0-9]+" value={form.version} disabled={!writer}
                onInput={(e) => setForm({ ...form, version: (e.target as HTMLInputElement).value })} />
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-[12px] font-semibold text-ink2">{t("setup.vendor")}</span>
              <input class="field" required value={form.vendor} disabled={!writer} placeholder={b ? "" : t("setup.vendor.placeholder")}
                onInput={(e) => setForm({ ...form, vendor: (e.target as HTMLInputElement).value })} />
            </label>
            <label class="flex flex-col gap-1 sm:col-span-2">
              <span class="text-[12px] font-semibold text-ink2">{t("setup.description")}</span>
              <input class="field" value={form.description} disabled={!writer} placeholder={b ? t("setup.description.placeholder.b") : t("setup.description.placeholder.c")}
                onInput={(e) => setForm({ ...form, description: (e.target as HTMLInputElement).value })} />
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-[12px] font-semibold text-ink2">{t("setup.categories")}</span>
              <input class="field font-mono" value={form.categories} disabled={!writer} placeholder="temperature, environment"
                onInput={(e) => setForm({ ...form, categories: (e.target as HTMLInputElement).value })} />
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-[12px] font-semibold text-ink2">{t("setup.licence")}</span>
              <select class="field" value={form.license} disabled={!writer} onChange={(e) => setForm({ ...form, license: (e.target as HTMLSelectElement).value })}>
                <option value="proprietary">{t("setup.licence.proprietary")}</option>
                <option value="Apache-2.0">Apache 2.0</option>
                <option value="MIT">MIT</option>
              </select>
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-[12px] font-semibold text-ink2">{t("setup.website")}</span>
              <input class="field font-mono" type="url" value={form.website} disabled={!writer} placeholder="https://..."
                onInput={(e) => setForm({ ...form, website: (e.target as HTMLInputElement).value })} />
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-[12px] font-semibold text-ink2">{t("setup.support")}</span>
              <input class="field font-mono" type="email" value={form.support_email} disabled={!writer} placeholder="support@..."
                onInput={(e) => setForm({ ...form, support_email: (e.target as HTMLInputElement).value })} />
            </label>
            <LogicFields data={data} value={lf} set={setLf} writer={writer} protocolChoice={!b} />
            <GeoSetup data={data} value={gf} set={setGf} writer={writer} />
            <p class="m-0 text-[12.5px] text-muted sm:col-span-2">{b ? t("setup.b.writes") : t("setup.c.writes")}</p>
            {writer && <div class="sm:col-span-2"><button type="submit" class="btn btn-pri" disabled={busy}>{busy ? t("setup.creating") : t("setup.step.create")}</button></div>}
          </form>
          {err && <p class="mb-0 mt-3 text-[13px] text-crit">{err}</p>}
        </Step>
        {b ? (
          <>
            <Step n={2} title={t("setup.b.step2.title")}>
              <p class="mb-0 mt-1 text-dim">{t("setup.b.step2.text")}</p>
            </Step>
            <Step n={3} title={t("setup.b.step3.title")}>
              <p class="mb-0 mt-1 text-dim">{t("setup.b.step3.text")}</p>
            </Step>
            <Step n={4} title={t("setup.b.step4.title")}>
              <p class="mb-0 mt-1 text-dim">{t("setup.b.step4.text")}</p>
            </Step>
          </>
        ) : (
          <>
            <Step n={2} title={t("setup.c.step2.title")}>
              <p class="mb-0 mt-1 text-dim">{t("setup.c.step2.text")}</p>
            </Step>
            <Step n={3} title={t("setup.c.step3.title")}>
              <p class="mb-0 mt-1 text-dim">{t("setup.c.step3.text")}</p>
            </Step>
            <Step n={4} title={t("setup.write.decoder")}>
              <p class="mb-0 mt-1 text-dim">{t("setup.c.step4.text")}</p>
            </Step>
          </>
        )}
      </div>
    </div>
  );
}

function SetupPanelA({ data, actions, writer }: { data: WorkbenchData; actions: Actions; writer: boolean }) {
  const st = data.setup!;
  const file = useRef<HTMLInputElement>(null);
  const preferred = st.messages.find((m) => /\.Uplink$/.test(m)) ?? st.messages[0] ?? "";
  const [form, setForm] = useState({ root: preferred, version: "1.0.0", vendor: data.me.org, description: "", categories: "", license: "proprietary", website: "", support_email: "" });
  const [lf, setLf] = useState<LogicForm>(noLogic);
  const [gf, setGf] = useState<GeoForm>(() => geoForm(data));
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const hasProto = st.protoFiles.length > 0;
  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await actions.setup({ ...form, ...logicFields(lf), ...geoFields(data, gf), root: form.root || preferred,
        categories: form.categories.split(",").map((c) => c.trim().toLowerCase()).filter(Boolean) });
    } catch (x) {
      setErr((x as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div class="min-h-0 flex-1 overflow-y-auto">
      <div class="mx-auto flex max-w-[760px] flex-col gap-7 px-6 py-10">
        <header>
          <h2 class="m-0 text-[20px] font-semibold">{t("setup.title", { name: data.project.name })}</h2>
          <p class="mb-0 mt-1.5 text-muted">{t("setup.a.intro")}
            {" "}<a href="/help/scenario-a" target="_blank">{t("setup.manual.steps")}</a>.</p>
        </header>
        <Step n={1} title={t("setup.a.step1.title")} done={hasProto}>
          <p class="mb-3 mt-1 text-muted">{t("setup.a.step1.text")}</p>
          {hasProto && <p class="m-0 mb-3 font-mono text-[12.5px] text-accent2">{st.protoFiles.join(", ")}</p>}
          {writer && (
            <>
              <button type="button" class="btn" onClick={() => file.current?.click()}><Icon name="plus" size={14} stroke={2.2} />{hasProto ? t("setup.a.addmore") : t("setup.a.upload")}</button>
              <input ref={file} type="file" multiple accept=".proto,.bin,.hex,.json" class="hidden" onChange={async (e) => {
                const el = e.target as HTMLInputElement;
                const files = [...(el.files ?? [])];
                el.value = "";
                if (files.length) {
                  try {
                    await actions.uploadSources(files);
                  } catch (x) {
                    setErr((x as Error).message);
                  }
                }
              }} />
            </>
          )}
          {st.error && hasProto && <p class="mb-0 mt-3 text-[13px] text-crit">{st.error}</p>}
        </Step>
        <Step n={2} title={t("setup.a.step2.title")}>
          {!hasProto ? (
            <p class="mb-0 mt-1 text-dim">{t("setup.a.afterupload")}</p>
          ) : (
            <form onSubmit={submit} class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label class="flex flex-col gap-1 sm:col-span-2">
                <span class="text-[12px] font-semibold text-ink2">{t("setup.a.root")}</span>
                <select class="field font-mono" value={form.root || preferred} disabled={!writer}
                  onChange={(e) => setForm({ ...form, root: (e.target as HTMLSelectElement).value })}>
                  {st.messages.map((m) => <option value={m}>{m}</option>)}
                </select>
              </label>
              <label class="flex flex-col gap-1">
                <span class="text-[12px] font-semibold text-ink2">{t("setup.version")}</span>
                <input class="field font-mono" required pattern="[0-9]+\.[0-9]+\.[0-9]+" value={form.version} disabled={!writer}
                  onInput={(e) => setForm({ ...form, version: (e.target as HTMLInputElement).value })} />
              </label>
              <label class="flex flex-col gap-1">
                <span class="text-[12px] font-semibold text-ink2">{t("setup.vendor")}</span>
                <input class="field" required value={form.vendor} disabled={!writer}
                  onInput={(e) => setForm({ ...form, vendor: (e.target as HTMLInputElement).value })} />
              </label>
              <label class="flex flex-col gap-1 sm:col-span-2">
                <span class="text-[12px] font-semibold text-ink2">{t("setup.description")}</span>
                <input class="field" value={form.description} disabled={!writer} placeholder={t("setup.description.placeholder.a")}
                  onInput={(e) => setForm({ ...form, description: (e.target as HTMLInputElement).value })} />
              </label>
              <label class="flex flex-col gap-1">
                <span class="text-[12px] font-semibold text-ink2">{t("setup.categories")}</span>
                <input class="field font-mono" value={form.categories} disabled={!writer} placeholder="level, sanitation, gnss"
                  pattern="\s*[a-z][a-z0-9-]{1,31}(\s*,\s*[a-z][a-z0-9-]{1,31}){0,4}\s*"
                  onInput={(e) => setForm({ ...form, categories: (e.target as HTMLInputElement).value })} />
              </label>
              <label class="flex flex-col gap-1">
                <span class="text-[12px] font-semibold text-ink2">{t("setup.licence")}</span>
                <select class="field" value={form.license} disabled={!writer} onChange={(e) => setForm({ ...form, license: (e.target as HTMLSelectElement).value })}>
                  <option value="proprietary">{t("setup.licence.proprietary")}</option>
                  <option value="Apache-2.0">Apache 2.0</option>
                  <option value="MIT">MIT</option>
                </select>
              </label>
              <label class="flex flex-col gap-1">
                <span class="text-[12px] font-semibold text-ink2">{t("setup.website")}</span>
                <input class="field font-mono" type="url" value={form.website} disabled={!writer} placeholder="https://..."
                  onInput={(e) => setForm({ ...form, website: (e.target as HTMLInputElement).value })} />
              </label>
              <label class="flex flex-col gap-1">
                <span class="text-[12px] font-semibold text-ink2">{t("setup.support")}</span>
                <input class="field font-mono" type="email" value={form.support_email} disabled={!writer} placeholder="support@..."
                  onInput={(e) => setForm({ ...form, support_email: (e.target as HTMLInputElement).value })} />
              </label>
              <LogicFields data={data} value={lf} set={setLf} writer={writer} protocolChoice={false} />
              <GeoSetup data={data} value={gf} set={setGf} writer={writer} />
              <p class="m-0 text-[12.5px] text-muted sm:col-span-2">{t("setup.a.writes")}</p>
              {writer && (
                <div class="sm:col-span-2">
                  <button type="submit" class="btn btn-pri" disabled={busy}>{busy ? t("setup.a.writing") : t("setup.a.create")}</button>
                </div>
              )}
            </form>
          )}
          {err && <p class="mb-0 mt-3 text-[13px] text-crit">{err}</p>}
        </Step>
        <Step n={3} title={t("setup.classify")}>
          <p class="mb-0 mt-1 text-dim">{t("setup.a.step3.text")}</p>
        </Step>
      </div>
    </div>
  );
}

export function EmptyReview({ data, actions, writer, onDesign }: { data: WorkbenchData; actions: Actions; writer: boolean; onDesign?: () => void }) {
  const [err, setErr] = useState("");
  const running = data.live.running;
  const c = data.project.scenario === "C";
  if (data.project.scenario === "B" && !running) {
    const req = data.requirements;
    const hasProto = data.files.some((f) => f.path.startsWith("proto/") && f.path.endsWith(".proto"));
    const [title, text] = !req || req.version === 0
      ? [t("setup.empty.b.start.title"), t("setup.empty.b.start.text")]
      : req.status !== "confirmed"
        ? [t("setup.empty.b.wait.title"), t("setup.empty.b.wait.text")]
        : !hasProto
          ? [t("setup.empty.b.protocol.title"), t("setup.empty.b.protocol.text")]
          : [t("setup.empty.classify.title"), t("setup.empty.b.classify.text")];
    return (
      <div class="flex min-h-0 flex-1 items-center justify-center overflow-y-auto p-8">
        <div class="max-w-[560px] text-center">
          <span class="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-xl bg-sel text-accent2"><Icon name="design" size={24} stroke={1.8} /></span>
          <h2 class="mb-1.5 mt-4 text-[18px] font-semibold">{title}</h2>
          <p class="m-0 text-muted">{text}</p>
          <div class="mt-5 flex justify-center gap-2">
            {onDesign && <button type="button" class={`btn ${hasProto && req?.status === "confirmed" ? "" : "btn-pri"}`} onClick={onDesign}>{t("setup.empty.opendesign")}</button>}
            {writer && hasProto && req?.status === "confirmed" && (
              <button type="button" class="btn btn-pri" disabled={data.aiPaused} onClick={async () => {
                setErr("");
                try {
                  await actions.send(CLASSIFY_PROMPT + " Then generate samples (add_sample) and run them.");
                } catch (x) {
                  setErr((x as Error).message);
                }
              }}><Icon name="spark" size={14} stroke={2} />{t("setup.classify")}</button>
            )}
          </div>
          {err && <p class="mb-0 mt-3 text-[13px] text-crit">{err}</p>}
        </div>
      </div>
    );
  }
  return (
    <div class="flex min-h-0 flex-1 items-center justify-center overflow-y-auto p-8">
      <div class="max-w-[560px] text-center">
        <span class="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-xl bg-sel text-accent2"><Icon name="spark" size={24} stroke={1.8} /></span>
        <h2 class="mb-1.5 mt-4 text-[18px] font-semibold">{running ? t("setup.empty.running.title") : c ? t("setup.empty.c.title") : t("setup.empty.classify.title")}</h2>
        <p class="m-0 text-muted">
          {running
            ? t("setup.empty.running.text")
            : c
              ? t("setup.empty.c.text")
              : t("setup.empty.a.text")}
        </p>
        {writer && !running && (
          <button type="button" class="btn btn-pri mt-5" disabled={data.aiPaused} onClick={async () => {
            setErr("");
            try {
              await actions.send(c ? DECODER_PROMPT : CLASSIFY_PROMPT);
            } catch (x) {
              setErr((x as Error).message);
            }
          }}>
            <Icon name="spark" size={14} stroke={2} />{c ? t("setup.write.decoder") : t("setup.classify")}
          </button>
        )}
        {data.aiPaused && <p class="mb-0 mt-3 text-[13px] text-warn">{t("setup.empty.paused")}</p>}
        {err && <p class="mb-0 mt-3 text-[13px] text-crit">{err}</p>}
      </div>
    </div>
  );
}
